'use client';

import { cn } from '@ui/lib/utils';
import { ChevronDown, ChevronUp } from 'lucide-react';
import { type ReactElement, type ReactNode } from 'react';
import { Pagination } from './Pagination';

export interface ColumnProps<T = any> {
  field?: keyof T | string;
  header?: ReactNode;
  body?: (rowData: T) => ReactNode;
  sortable?: boolean;
  className?: string;
}

export function Column<T = any>(_props: ColumnProps<T>): ReactElement | null {
  return null;
}

export interface DataTableProps<T = any> {
  value: T[];
  header?: ReactNode;
  paginator?: boolean;
  rows?: number;
  totalRecords?: number;
  currentPage?: number;
  onPageChange?: (page: number) => void;
  sortField?: string;
  sortOrder?: 'asc' | 'desc';
  onSort?: (field: string, order: 'asc' | 'desc') => void;
  children: ReactNode;
  className?: string;
  emptyMessage?: string;
  loading?: boolean;
  dictionary?: Record<string, string>;
}

export function DataTable<T = any>({
  value,
  header,
  paginator = false,
  rows = 10,
  totalRecords,
  currentPage = 1,
  onPageChange,
  sortField,
  sortOrder,
  onSort,
  children,
  className,
  emptyMessage = 'No records found',
  loading = false,
  dictionary = {},
}: DataTableProps<T>) {
  const columns: ColumnProps<T>[] = [];

  const extractColumns = (node: ReactNode): void => {
    if (!node) return;

    if (Array.isArray(node)) {
      node.forEach(extractColumns);
      return;
    }

    if (typeof node === 'object' && 'type' in node) {
      const element = node as ReactElement;
      if (element.type === Column) {
        columns.push(element.props as ColumnProps<T>);
      }
    }
  };

  extractColumns(children);

  const handleSort = (field: string) => {
    if (!onSort) return;
    const newOrder = sortField === field && sortOrder === 'asc' ? 'desc' : 'asc';
    onSort(field, newOrder);
  };

  const renderSortIcon = (field: string) => {
    if (sortField !== field) return null;
    return sortOrder === 'asc' ? (
      <ChevronUp className="inline-block ml-1 h-4 w-4" />
    ) : (
      <ChevronDown className="inline-block ml-1 h-4 w-4" />
    );
  };

  const total = totalRecords ?? value.length;
  const totalPages = Math.ceil(total / rows);
  const startIndex = (currentPage - 1) * rows;
  const endIndex = startIndex + rows;
  const pageData = paginator ? value.slice(startIndex, endIndex) : value;

  return (
    <div className={cn('flex flex-col', className)}>
      <div className="overflow-x-auto">
        <div className="inline-block min-w-full align-middle">
          <div className="overflow-hidden border border-gray-200 rounded-lg">
            {header && (
              <div className="px-6 py-4 border-b border-gray-200">
                <h3 className="text-lg font-medium text-gray-900">{header}</h3>
              </div>
            )}
            <table className="min-w-full divide-y divide-gray-200">
              <thead className="bg-gray-50">
                <tr>
                  {columns.map((col, index) => (
                    <th
                      key={index}
                      onClick={() => col.sortable && col.field && handleSort(col.field as string)}
                      className={cn(
                        'px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider',
                        col.sortable && 'cursor-pointer hover:bg-gray-100 transition-colors',
                        col.className
                      )}
                      scope="col"
                    >
                      <div className="flex items-center">
                        {col.header}
                        {col.sortable && col.field && renderSortIcon(col.field as string)}
                      </div>
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                {loading ? (
                  <tr>
                    <td colSpan={columns.length} className="px-6 py-8 text-center text-gray-500">
                      <div className="flex items-center justify-center">
                        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary-600" />
                        <span className="ml-3">Loading...</span>
                      </div>
                    </td>
                  </tr>
                ) : pageData.length === 0 ? (
                  <tr>
                    <td colSpan={columns.length} className="px-6 py-8 text-center text-gray-500">
                      {emptyMessage}
                    </td>
                  </tr>
                ) : (
                  pageData.map((rowData, rowIndex) => (
                    <tr key={rowIndex} className="hover:bg-gray-50 transition-colors">
                      {columns.map((col, colIndex) => (
                        <td
                          key={colIndex}
                          className={cn(
                            'px-6 py-4 whitespace-nowrap text-sm text-gray-900',
                            col.className
                          )}
                        >
                          {col.body
                            ? col.body(rowData)
                            : col.field
                              ? (rowData as any)[col.field]
                              : null}
                        </td>
                      ))}
                    </tr>
                  ))
                )}
              </tbody>
            </table>

            {paginator && total > rows && (
              <div className="px-6 py-4 border-t border-gray-200 bg-gray-50">
                <Pagination
                  dictionary={dictionary}
                  currentPage={currentPage}
                  totalPages={totalPages}
                  baseUrl=''
                // onPageChange={onPageChange || (() => {})}
                // showInfo
                // totalItems={total}
                // itemsPerPage={rows}
                />
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
